/**
 * Provider Management Component
 * 
 * Main component for managing providers. Handles CRUD operations,
 * URL-based filtering, and coordinates between list and form views.
 * 
 * @component
 * @example
 * return <Provider />
 */

import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import CustomContainer from '../common/CustomContainer';
import ProviderForm from './provider-form';
import ProviderList from './provider-list';
import ProviderService from '../../services/provider-service';
import { getFacilities } from '../../utils/apiClient';
import { useURLFiltering, useCRUDOperations } from '../../custom-hooks';

/**
 * Provider Management Main Component
 */
const Provider = () => {
  const [facilityMap, setFacilityMap] = useState({});

  // Use custom hooks for common functionality
  const {
    selectedFilters,
    updateFilters,
    getAPIParams
  } = useURLFiltering('provider');

  const {
    data: providers,
    loading,
    submitting: isSubmitting,
    renderType,
    initialValues,
    selectedItem,
    fetchData,
    refreshData,
    handleSubmit: handleCRUDSubmit,
    handleEdit: handleCRUDEdit,
    handleAdd: handleCRUDAdd,
    handleCancel,
    setRenderType
  } = useCRUDOperations(ProviderService, 'provider');

  /**
   * Fetches facilities and creates facility map
   */
  const fetchFacilities = async () => {
    try {
      const facilities = await getFacilities();
      const map = {};
      facilities.forEach(fac => { 
        map[fac._id] = fac.facilityName; 
      });
      setFacilityMap(map);
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  };

  /**
   * Handles filter changes with API integration
   * 
   * @param {Object} newFilters - New filter values
   */
  const handleFiltersChange = (newFilters) => {
    try {
      updateFilters(newFilters); // This will trigger useEffect to call fetchData
    } catch (error) {
      console.error('Error applying filters:', error);
      message.error('Failed to apply filters. Please try again.');
    }
  };

  /**
   * Refreshes provider data with current filters
   * 
   * @returns {Promise<Array>} Refreshed provider data
   */
  const handleRefresh = async () => {
    const apiParams = getAPIParams(selectedFilters);
    return await refreshData(apiParams);
  };

  /**
   * Handles editing a provider
   * 
   * @param {Object} provider - Provider to edit
   */
  const handleEdit = (provider) => {
    // Filter out inactive facilities from affiliations
    if (provider.affiliations && Array.isArray(provider.affiliations)) {
      const filteredAffiliations = provider.affiliations.filter(
        affiliation => facilityMap[affiliation.facilityId]
      );
      provider = { ...provider, affiliations: filteredAffiliations };
    }
    
    // Let the transformation service handle the data mapping
    // This ensures consistency with the new affiliation structure
    handleCRUDEdit(provider);
  };

  /**
   * Handles adding a new provider (clears form data)
   */
  const handleAdd = () => {
    // Use the CRUD hook's handleAdd to properly clear all form state
    handleCRUDAdd();
    console.log('✅ Add Provider clicked - form state cleared');
  };

  /**
   * Handles form submission for create/update
   * 
   * @param {Object} data - Form data to submit
   */
  const handleSubmit = async (data) => {
    const result = await handleCRUDSubmit(data, handleRefresh);
    return result;
  };

  /**
   * Handles back navigation
   */
  const handleBack = () => {
    handleCancel();
  };

  // Initialize data on component mount
  useEffect(() => {
    const apiParams = getAPIParams(selectedFilters);
    fetchFacilities();
    fetchData(apiParams);
  }, [selectedFilters, fetchData, getAPIParams]);

  return (
    // <CustomContainer containerWidth={renderType === 'list' ? '90%' : '80%'}>
    <CustomContainer containerWidth="95%">
      {renderType === 'list' ? (
        <ProviderList 
          setInitialValues={() => {}} // Legacy prop, not used
          setRenderType={setRenderType} 
          renderType={renderType} 
          providers={providers} 
          loading={loading} 
          onEdit={handleEdit}
          onAdd={handleAdd} // Pass the proper handleAdd function
          facilityMap={facilityMap}
          onRefresh={handleRefresh}
          selectedFilters={selectedFilters}
          onFiltersChange={handleFiltersChange}
        />
      ) : (
        <ProviderForm 
          onSubmit={handleSubmit} 
          handleBack={handleBack} 
          setRenderType={setRenderType} 
          renderType={renderType} 
          initialValues={initialValues}
          isSubmitting={isSubmitting}
          provider={selectedItem}
          onRefresh={handleRefresh}
        />
      )}
    </CustomContainer>
  );
};

export default Provider;